/** Browser authority binding for the shared, in-memory manual command contract. */
import { ApiError, apiFetch, captureFeedbackConversationBoundary, isCurrentResourceCommandScope } from './api';
import { removeCoachingConsumerReads } from './queryClient';
import { coachingManualClient } from '../../../contracts/coaching-companion/v1/client';
import { captureCoachingManualOperation } from '../../../contracts/coaching-companion/v1/manualCommands';
import type { CoachingTransport } from '../../../contracts/coaching-companion/v1/client';
import type { CoachingScopeIntent } from '../../../contracts/coaching-companion/v1/scope';
import type { CoachingManualCommand, CoachingManualResult } from '../../../contracts/coaching-companion/v1/manualCommands';
import type { ResourceReadScope, ResolveCanonicalFocusRequest } from './types';

function intent(scope: ResourceReadScope): CoachingScopeIntent {
  if (!scope.actorId || !scope.clubId || !scope.teamId || scope.seasonId === '') throw new RangeError('invalid_manual_scope');
  return { team_id: scope.teamId, season: scope.seasonId === null ? { mode: 'unseasoned' }
    : { mode: 'current', season_id: scope.seasonId } };
}
const abort = () => new DOMException('Manual coaching context changed', 'AbortError');
function transport(current: () => boolean, signal?: AbortSignal): CoachingTransport {
  const check = () => { if (!current() || signal?.aborted) throw abort(); };
  return {
    async request<T>({ method, path, query, body }: Parameters<CoachingTransport['request']>[0]): Promise<T> {
      check();
      try {
        const value = await apiFetch<T>(path, { method, query, body, scope: false, cache: 'no-store', signal });
        check();
        return value;
      } catch (error) { check(); throw error; }
    },
  };
}

/** Pure reference/support reads cannot acknowledge or dispatch a manual write. */
export function coachingManualReads(scope: ResourceReadScope) {
  const captured = { ...scope }; const selected = intent(captured);
  const boundary = captureFeedbackConversationBoundary({ ...captured, view: 'coach' });
  async function read<T>(call: (client: ReturnType<typeof coachingManualClient>) => Promise<T>, signal?: AbortSignal) {
    try { return await call(coachingManualClient(transport(boundary.current, signal), selected)); }
    catch (error) {
      if (!boundary.current() || signal?.aborted) throw abort();
      if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
        await removeCoachingConsumerReads(captured).catch(() => {});
        if (!boundary.current() || signal?.aborted) throw abort();
      }
      throw error;
    }
  }
  return {
    resolveFocus: (activityId: string, body: ResolveCanonicalFocusRequest, signal?: AbortSignal) =>
      read(client => client.resolveFocus(activityId, body), signal),
    listFocuses: (activityId: string, signal?: AbortSignal) => read(client => client.listFocuses(activityId), signal),
    readSupport: (supportId: string, revisionId: string, playerId: string, eventId?: string, signal?: AbortSignal) =>
      read(client => client.readSupport(supportId, revisionId, playerId, eventId), signal),
    dispose: boundary.dispose,
  };
}

export function captureCoachingManualCommand<C extends CoachingManualCommand>(scope: ResourceReadScope, command: C) {
  const captured = { ...scope }; const selected = intent(captured);
  const boundary = captureFeedbackConversationBoundary({ ...captured, view: 'coach' });
  const current = () => boundary.current() && isCurrentResourceCommandScope(captured);
  let signal: AbortSignal | undefined;
  let inFlight: Promise<CoachingManualResult<C>> | null = null;
  const dynamicTransport: CoachingTransport = {
    request: <T>(request: Parameters<CoachingTransport['request']>[0]) => transport(current, signal).request<T>(request),
  };
  let operation: ReturnType<typeof captureCoachingManualOperation<C>>;
  try { operation = captureCoachingManualOperation(dynamicTransport, selected, command,
    { current, dispose: boundary.dispose }, {
      classifyFailure: error => {
        const code = error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail
          ? error.detail.code : null;
        if (code === 'manual_operation_retired') return 'retired';
        if (code === 'manual_operation_unresolved') return 'unknown';
        return error instanceof ApiError && error.status >= 400 && error.status < 500 ? 'rejected' : 'unknown';
      },
      onAccepted: () => removeCoachingConsumerReads(captured),
    }); } catch (error) { boundary.dispose(); throw error; }
  return {
    getIntent: operation.getIntent,
    getState: operation.getState,
    send(nextSignal?: AbortSignal): Promise<CoachingManualResult<C>> {
      if (!current() || nextSignal?.aborted) return Promise.reject(abort());
      if (inFlight) return inFlight;
      signal = nextSignal;
      inFlight = operation.send().catch(error => { if (!current()) throw abort(); throw error; })
        .finally(() => { signal = undefined; inFlight = null; });
      return inFlight;
    },
    dispose: operation.dispose,
  };
}
